<script>
import Layout from '@/router/layouts/main.vue';
import Grid from './components/grid';
import CardsList from './components/cardsList.vue';
import statusMixin from '@/mixins/statusMixin';
import StatusMultiselect from '@/components/widgets/multiselects/status';
import FiltersCard from '@/components/widgets/cards/filters-search';
import FieldsMenu from '@/components/widgets/grids/grid-fields-menu.vue';
import AddButton from './components/add-button.vue';

/**
 * Dashboard Component
 */
export default {
  page: {
    title: 'WorkOrders',
  },
  mixins: [statusMixin],
  components: {
    Layout,
    Grid,
    CardsList,
    StatusMultiselect,
    FiltersCard,
    FieldsMenu,
    AddButton,
  },
  data() {
    return {
      filters: null,
      searchText: '',
      showFilters: false,
    };
  },
  created() {
    this.search();
  },
  methods: {
    search() {
      this.filters = {
        statusCodes: this.selectedStatusIds,
        search: this.searchText,
      };
    },
    cancelSearch() {
      this.selectedStatus = [];
      this.searchText = '';

      this.search();
    },
  },
  computed: {
    fieldsComputed() {
      return [
        {
          key: 'name',
          label: this.$t('general.name'),
          sortable: true,
        },
        {
          key: 'statusOrder',
          label: this.$t('general.status'),
          sortable: true,
          class: 'centered-field',
        },
        {
          key: 'totalItemsPrice',
          label: this.$t('items.price'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'requestedItemsQuantity',
          label: this.$t('items.requestedNoEn'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'orderedItemsQuantity',
          label: this.$t('items.orderedNoEn'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'reservedTotal',
          label: this.$t('items.reservedTotal'),
          class: 'centered-field',
        },
        {
          key: 'consumedItemsQuantity',
          label: this.$t('items.consumed'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'deliveryDate',
          label: this.$t('general.deliveryDate'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'consume',
          label: this.$t('general.consume'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'manage',
          label: this.$t('general.manage'),
          class: 'centered-field',
          sortable: true,
        },
        {
          key: 'delete',
          label: this.$t('general.delete'),
          class: 'centered-field',
          sortable: true,
        },
      ];
    },
    workOrdersFiltersConfig: {
      get() {
        return this.$store.getters['localstorage/getByCode'](
          'workOrdersFiltersConfig'
        );
      },
      set(value) {
        this.$store.dispatch('localstorage/setByCode', {
          code: 'workOrdersFiltersConfig',
          config: value,
        });
      },
    },
    listType: {
      get() {
        return this.workOrdersFiltersConfig.listType;
      },
      set(value) {
        this.workOrdersFiltersConfig = {
          ...this.workOrdersFiltersConfig,
          listType: value,
        };
      },
    },
    selectedStatus: {
      get() {
        return this.workOrdersFiltersConfig.selectedStatus;
      },
      set(value) {
        this.workOrdersFiltersConfig = {
          ...this.workOrdersFiltersConfig,
          selectedStatus: value,
        };
      },
    },
    selectedStatusIds() {
      return this.selectedStatus != null
        ? this.selectedStatus.map((a) => a.statusCode)
        : [];
    },
  },
};
</script>

<template>
  <Layout
    :title="$t('workOrders.workOrders')"
    :no-card="true"
    @esc-double-pressed="cancelSearch"
  >
    <AddButton @reload-work-orders="search()" />

    <div class="flex-box">
      <b-input-group class="search-bar custom-inventory-shadow">
        <b-form-input
          id="searchInput"
          type="text"
          style="border: none"
          :placeholder="$t('general.search')"
          @keydown.escape.prevent="cancelSearch()"
          @keydown.enter.prevent="search()"
          v-model="searchText"
        ></b-form-input>
        <b-input-group-append>
          <b-button
            style="border: none"
            variant="outline-light"
            @click="search()"
          >
            <i class="bx bx-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <b-button
        class="flex-push custom-inventory-shadow"
        @click="listType = 'columns'"
        :variant="listType !== 'list' ? 'primary' : 'light'"
        style="height: 40px; margin-right: 10px"
      >
        <i class="fas fa-columns"></i>
        {{ $t('general.columns') }}
      </b-button>
      <b-button
        class="custom-inventory-shadow"
        @click="listType = 'list'"
        :variant="listType === 'list' ? 'primary' : 'light'"
        style="height: 40px; margin-right: 10px"
      >
        <i class="fas fa-list"></i>
        {{ $t('general.list') }}
      </b-button>

      <FieldsMenu
        id="work-orders-grid"
        :dropup="true"
        :fields="fieldsComputed"
      />
    </div>

    <FiltersCard @search="search" @cancel="cancelSearch">
      <template #row>
        <b-row>
          <b-col sm="12" lg="4">
            <b-form-group
              id="group-status"
              :label="$t('general.status')"
              label-class="no-bold"
              label-for="statusSelect"
            >
              <StatusMultiselect
                :list="getWorkOrderStatus()"
                :defaultSelectedList="selectedStatus"
                @selection-change="(l) => (selectedStatus = l)"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </template>
    </FiltersCard>

    <Grid
      v-if="listType === 'list' && filters != null"
      :filters="filters"
      :fields="fieldsComputed"
    />

    <CardsList v-if="listType !== 'list'" :filters="filters" />
  </Layout>
</template>

<style scoped>
.search-bar {
  max-width: 500px;
  max-height: 40px;
  background-color: white;
  border-radius: 5px;
}
</style>